/**
 * Audio silence detection - shared between frontend and backend
 */

// ============================================================================
// Types
// ============================================================================

export interface SilenceDetectionResult {
  /** Whether the audio is considered silent */
  isSilent: boolean;
  /** Duration of detected speech in seconds */
  speechDuration: number;
  /** Duration of detected silence in seconds */
  silenceDuration: number;
  /** Percentage of audio that contains speech (0-100) */
  speechPercentage: number;
  /** Whether this audio should be transcribed */
  shouldTranscribe: boolean;
  metadata?: {
    averageRMS?: number;
    averageZCR?: number;
    maxZCR?: number;
    minZCR?: number;
    spectralCentroid?: number;
  };
}

export interface SilenceDetectionConfig {
  /** Silence threshold in dB (e.g., -40) */
  threshold: number;
  /** Minimum duration of speech to consider "not silent" in milliseconds */
  minSpeechDuration: number;
  /** Minimum percentage of speech to consider "not silent" (0-100) */
  minSpeechPercentage: number;
  /** Maximum Zero-Crossing Rate threshold (0-1). Higher ZCR indicates noise/unvoiced sounds. Default: 0.4 */
  maxZCR?: number;
  /** Sample rate of audio (default: 16000) */
  sampleRate?: number;
  /** Window size for analysis (default: 1024) */
  windowSize?: number;
}

export interface AudioMetadata {
  originalSize: number;
  speechPercentage: number;
  timestamp: number;
  duration: number;
  sampleRate: number;
  channels: number;
}

export interface WindowAnalysisResult {
  index: number;
  rms: number;
  zcr: number;
  isSpeech: boolean;
  energyDB: number;
}

export interface SilenceRange {
  startSample: number;
  endSample: number;
  duration: number;
  type: 'leading' | 'trailing' | 'internal';
}

export interface SpeechCheckResult {
  hasSpeech: boolean;
  rms: number;
  energyDB: number;
  zcr: number;
}

// ============================================================================
// Configuration
// ============================================================================

export const DEFAULT_SILENCE_DETECTION_CONFIG: SilenceDetectionConfig = {
  threshold: -40,              // dB
  minSpeechDuration: 300,      // ms
  minSpeechPercentage: 5,      // %
  maxZCR: 0.4,                 // Zero-crossing rate (filters noise/unvoiced sounds)
  sampleRate: 16000,           // Hz
  windowSize: 1024,            // samples
};

// ============================================================================
// Audio Silence Detector
// ============================================================================

export class AudioSilenceDetector {
  private config: SilenceDetectionConfig;

  constructor(config?: Partial<SilenceDetectionConfig>) {
    this.config = {
      ...DEFAULT_SILENCE_DETECTION_CONFIG,
      ...config,
    };
  }

  /**
   * Detect if audio is predominantly silent
   */
  public detectSilence(samples: Float32Array): SilenceDetectionResult {
    const windowResults = this.analyzeWindows(samples);
    const speechWindows = windowResults.filter(w => w.isSpeech).length;
    const totalWindows = windowResults.length;
    const speechPercentage = (speechWindows / totalWindows) * 100;

    const samplesPerWindow = this.config.windowSize!;
    const sampleRate = this.config.sampleRate!;
    const totalDuration = samples.length / sampleRate;
    const speechDuration = (speechWindows * samplesPerWindow) / sampleRate;
    const silenceDuration = totalDuration - speechDuration;

    // Audio is silent ONLY if BOTH conditions are true:
    // 1. Very short speech duration (< 300ms) AND
    // 2. Very low speech percentage (< 5%)
    // This allows short but clear commands like "Go!" to be accepted
    const isSilent =
      speechDuration < this.config.minSpeechDuration / 1000 &&
      speechPercentage < this.config.minSpeechPercentage;

    // Calculate ZCR statistics for debugging
    const zcrValues = windowResults.map(r => r.zcr);
    const maxZCR = zcrValues.length > 0 ? Math.max(...zcrValues) : 0;
    const minZCR = zcrValues.length > 0 ? Math.min(...zcrValues) : 0;

    return {
      isSilent,
      speechDuration,
      silenceDuration,
      speechPercentage,
      shouldTranscribe: !isSilent,
      metadata: {
        averageRMS: this.calculateAverageRMS(windowResults),
        averageZCR: this.calculateAverageZCR(windowResults),
        maxZCR,
        minZCR,
      },
    };
  }

  /**
   * Analyze audio in sliding windows
   */
  private analyzeWindows(samples: Float32Array): WindowAnalysisResult[] {
    const windowSize = this.config.windowSize!;
    const maxZCR = this.config.maxZCR ?? 1.0; // Default: no ZCR filtering
    const results: WindowAnalysisResult[] = [];
    const totalWindows = Math.floor(samples.length / windowSize);

    for (let i = 0; i < totalWindows; i++) {
      const start = i * windowSize;
      const window = samples.slice(start, start + windowSize);

      const rms = this.calculateRMS(window);
      const zcr = this.calculateZCR(window);
      const energyDB = 20 * Math.log10(rms + Number.EPSILON);

      // Speech detection with dual criteria:
      // 1. Energy above threshold (loud enough)
      // 2. ZCR below threshold (not noise)
      const hasEnoughEnergy = energyDB > this.config.threshold;
      const isNotNoise = zcr < maxZCR;
      const isSpeech = hasEnoughEnergy && isNotNoise;

      results.push({
        index: i,
        rms,
        zcr,
        isSpeech,
        energyDB,
      });
    }

    return results;
  }

  /**
   * Check if a single audio frame contains speech
   *
   * This is optimized for real-time monitoring (checking small frames every 1-2 seconds)
   * Use this instead of detectSilence() when you only need to check current audio state
   *
   * @param samples Audio samples to analyze (typically from AnalyserNode.getFloatTimeDomainData())
   * @returns Speech check result with hasSpeech flag, RMS, energy in dB, and ZCR
   */
  public checkSpeech(samples: Float32Array): SpeechCheckResult {
    const rms = this.calculateRMS(samples);
    const zcr = this.calculateZCR(samples);
    const energyDB = 20 * Math.log10(rms + Number.EPSILON);
    const hasSpeech = energyDB > this.config.threshold;

    return {
      hasSpeech,
      rms,
      energyDB,
      zcr,
    };
  }

  /**
   * Calculate RMS (Root Mean Square) energy
   * Core algorithm for silence detection
   */
  public calculateRMS(samples: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
      sum += samples[i] * samples[i];
    }
    return Math.sqrt(sum / samples.length);
  }

  /**
   * Calculate Zero-Crossing Rate
   * Useful for distinguishing voiced vs unvoiced sounds
   */
  public calculateZCR(samples: Float32Array): number {
    let crossings = 0;
    for (let i = 1; i < samples.length; i++) {
      if ((samples[i] >= 0) !== (samples[i - 1] >= 0)) {
        crossings++;
      }
    }
    return crossings / samples.length;
  }

  private calculateAverageRMS(results: WindowAnalysisResult[]): number {
    if (results.length === 0) return 0;
    return results.reduce((acc, r) => acc + r.rms, 0) / results.length;
  }

  private calculateAverageZCR(results: WindowAnalysisResult[]): number {
    if (results.length === 0) return 0;
    return results.reduce((acc, r) => acc + r.zcr, 0) / results.length;
  }
}
